import { atom, useAtomValue, useSetAtom, useStore } from "jotai";
import { selectAtom } from "jotai/utils";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { AttachedFile } from "./types.ts";

export type ComposerDraft = {
  text: string;
  attachedFiles: AttachedFile[];
};

/**
 * Unsent composer drafts, keyed by thread id.
 *
 * A thread's composer is not one long-lived component. When a card takes over
 * the composer area the standalone Omnibox unmounts and the card's "Ask a
 * follow up" row mounts a new one; the swap runs in reverse when the card is
 * decided or dismissed. Local state dies with each instance, taking whatever
 * the user was typing with it (BIG-8541), so a thread composer keeps its draft
 * here and every instance for that thread reads the same entry. Session-only:
 * a reload starts clean.
 */
export const threadComposerDraftsAtom = atom<Record<number, ComposerDraft>>({});

const NO_FILES: AttachedFile[] = [];

const REFUSED_NEW_CHAT_DRAFT_KEY = "composer:refusedNewChatDraft";

/**
 * Hold, or with `undefined` or an empty string drop, the text of a new-chat
 * send that was refused for lack of AI usage. The upgrade dialog's two upgrade
 * paths leave the page — Stripe Checkout navigates away, the Billing panel
 * unmounts the composer — and a thread-less composer's draft is local state,
 * so without this the user would come back to an empty box. The next
 * thread-less composer to mount takes it. Session storage so it survives the
 * Checkout round trip; text only, because attachments cannot be serialized.
 */
export function setRefusedNewChatDraft(text: string | undefined): void {
  try {
    if (text) sessionStorage.setItem(REFUSED_NEW_CHAT_DRAFT_KEY, text);
    else sessionStorage.removeItem(REFUSED_NEW_CHAT_DRAFT_KEY);
  } catch {
    // Storage blocked or full: the refused text is simply not carried over.
  }
}

function getRefusedNewChatDraft(): string | undefined {
  try {
    return sessionStorage.getItem(REFUSED_NEW_CHAT_DRAFT_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

type Setter<T> = Dispatch<SetStateAction<T>>;

/**
 * The composer's draft text and attachments. With a `threadId` they live in
 * {@link threadComposerDraftsAtom} and outlive the component; without one (the
 * home and New chat composers, which have no thread yet) they are plain local
 * state, seeded from {@link setRefusedNewChatDraft} when a refused send left
 * text behind and no `seed` asks for something else; `restored` says the text
 * started that way, so the composer can treat it as typed rather than as a
 * prompt to expand.
 *
 * `seed` is what an empty thread composer starts with, mirroring `useState`'s
 * initial value: it is read only while the thread has no draft entry at all,
 * so a draft the user cleared stays cleared.
 */
export const useComposerDraft = (threadId: number | undefined, seed: string) => {
  // Read during init and dropped from an effect, so a double-invoked
  // initializer cannot consume it before the state it seeds is kept.
  const [restoredText] = useState(() =>
    threadId === undefined ? getRefusedNewChatDraft() : undefined,
  );
  // A seed is a fresh request, such as a deep link's prompt, so it wins.
  const restored = restoredText !== undefined && !seed;
  const [localText, setLocalText] = useState(restored ? (restoredText ?? seed) : seed);
  useEffect(() => {
    if (restoredText !== undefined) setRefusedNewChatDraft(undefined);
  }, [restoredText]);
  const [localFiles, setLocalFiles] = useState<AttachedFile[]>(NO_FILES);
  const draft = useAtomValue(
    useMemo(
      () =>
        selectAtom(threadComposerDraftsAtom, (drafts) =>
          threadId === undefined ? undefined : drafts[threadId],
        ),
      [threadId],
    ),
  );
  const setDrafts = useSetAtom(threadComposerDraftsAtom);

  const updateDraft = useCallback(
    (patch: (draft: ComposerDraft) => ComposerDraft) => {
      if (threadId === undefined) return;
      setDrafts((prev) => ({
        ...prev,
        [threadId]: patch(prev[threadId] ?? { text: seed, attachedFiles: NO_FILES }),
      }));
    },
    [threadId, seed, setDrafts],
  );
  const setThreadText = useCallback<Setter<string>>(
    (next) =>
      updateDraft((draft) => ({
        ...draft,
        text: typeof next === "function" ? next(draft.text) : next,
      })),
    [updateDraft],
  );
  const setThreadFiles = useCallback<Setter<AttachedFile[]>>(
    (next) =>
      updateDraft((draft) => ({
        ...draft,
        attachedFiles: typeof next === "function" ? next(draft.attachedFiles) : next,
      })),
    [updateDraft],
  );

  if (threadId === undefined) {
    return {
      text: localText,
      setText: setLocalText,
      attachedFiles: localFiles,
      setAttachedFiles: setLocalFiles,
      restored,
    };
  }
  return {
    text: draft?.text ?? seed,
    setText: setThreadText,
    attachedFiles: draft?.attachedFiles ?? NO_FILES,
    setAttachedFiles: setThreadFiles,
    restored: false,
  };
};

/**
 * Lets another surface pick up a thread's draft text: `peek` reads it and
 * `clear` empties it, leaving any attachments where they are. Two steps so a
 * caller can read during render and clear from an effect. Reads the store
 * directly rather than subscribing, so the caller does not re-render on every
 * keystroke in the composer.
 */
export const useComposerDraftHandoff = (threadId: number) => {
  const store = useStore();
  const peek = useCallback(
    () => store.get(threadComposerDraftsAtom)[threadId]?.text ?? "",
    [store, threadId],
  );
  const clear = useCallback(() => {
    const drafts = store.get(threadComposerDraftsAtom);
    const draft = drafts[threadId];
    if (!draft?.text) return;
    store.set(threadComposerDraftsAtom, { ...drafts, [threadId]: { ...draft, text: "" } });
  }, [store, threadId]);
  return { peek, clear };
};
